export type MovieRegion = '日本' | '韩国';

export type Movie = {
  id: string;
  title: string;
  originalTitle: string;
  englishTitle: string;
  region: MovieRegion;
  year: number;
  duration: number;
  rating: number;
  views: number;
  likes: number;
  director: string;
  cast: string[];
  category: string;
  tags: string[];
  summary: string;
  synopsis: string[];
  cover: string;
};

export type Category = {
  slug: string;
  name: string;
  en: string;
  icon: string;
  description: string;
  intro: string;
};

export type HeroSlide = {
  id: string;
  movieId: string;
  image: string;
  kicker: string;
  headline: string;
  caption: string;
};

export const categories: Category[] = [
  {
    slug: 'drama',
    name: '剧情片',
    en: 'Drama',
    icon: 'ri-film-line',
    description: '以人物关系与情感转折为核心的日韩剧情作品',
    intro: '剧情片是本站收录最完整的类别，以细腻的人物关系、克制的镜头语言和完整的故事结构见长。这里的作品大多围绕都市男女的相遇与告别展开，适合喜欢慢慢进入故事的观众。',
  },
  {
    slug: 'romance',
    name: '恋爱片',
    en: 'Romance',
    icon: 'ri-heart-3-line',
    description: '聚焦亲密关系与心动瞬间的恋爱题材影片',
    intro: '恋爱片以两人之间的情感推进为主线，镜头更偏向温暖色调与生活细节。收录作品覆盖初遇、暗恋、重逢等常见叙事结构，节奏轻缓，适合夜间独自观看。',
  },
  {
    slug: 'suspense',
    name: '悬疑片',
    en: 'Suspense',
    icon: 'ri-spy-line',
    description: '带有反转结构与心理张力的悬疑类影片',
    intro: '悬疑类作品强调信息差与心理博弈，往往在最后二十分钟完成一次叙事反转。收录作品多以密闭空间、双线叙事为特色，观影时需要留意画面中的细节线索。',
  },
  {
    slug: 'urban',
    name: '都市片',
    en: 'Urban',
    icon: 'ri-building-4-line',
    description: '以东京、首尔等城市夜色为舞台的都市题材',
    intro: '都市片把城市本身当作角色，霓虹、地铁末班车、二十四小时便利店构成了故事发生的全部舞台。这类作品画面质感强，配乐与城市噪音的层次处理是主要看点。',
  },
  {
    slug: 'period',
    name: '时代剧',
    en: 'Period',
    icon: 'ri-ancient-gate-line',
    description: '昭和与近代背景下的复古氛围作品',
    intro: '时代剧在美术与服装上投入较多，从木质旅馆到旧式电车站都做了细致还原。叙事节奏相对缓慢，更适合喜欢复古氛围与胶片质感的观众。',
  },
  {
    slug: 'rookie',
    name: '新人映画',
    en: 'Newcomer',
    icon: 'ri-sparkling-line',
    description: '首次担纲主演的新人演员作品合集',
    intro: '新人映画收录当期首次担纲主演的演员作品，题材偏向轻快与生活化。这一类别更新频率最高，也是观察日韩影视新人风格的第一站。',
  },
];

export const allTags: string[] = [
  '高分推荐',
  '唯美摄影',
  '情感细腻',
  '反转结局',
  '城市夜景',
  '人气演员',
  '经典重制',
  '女性视角',
  '长镜头',
  '治愈系',
  '雨夜',
  '复古胶片',
  '密闭空间',
  '双线叙事',
];

export const heroSlides: HeroSlide[] = [
  {
    id: 'hero-1',
    movieId: 'rain-night-confession',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20elegant%20woman%20in%20a%20deep%20burgundy%20coat%20standing%20beside%20a%20rain%20covered%20window%20in%20a%20dim%20Tokyo%20apartment%20at%20night%2C%20red%20neon%20reflections%20on%20wet%20glass%2C%20moody%20film%20noir%20lighting%2C%20warm%20amber%20lamp%20glow%2C%20tasteful%20fully%20clothed%20editorial%20film%20photography%2C%20shallow%20depth%20of%20field%2C%2035mm%20grain&width=1600&height=900&seq=hero-rain-night-01&orientation=landscape',
    kicker: '本周焦点 · 日本',
    headline: '雨夜的告白',
    caption: '一场持续整夜的雨，把两个陌生人困在同一间旧公寓里。',
  },
  {
    id: 'hero-2',
    movieId: 'seoul-3am',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20night%20view%20of%20a%20rainy%20Seoul%20crosswalk%20with%20a%20well%20dressed%20woman%20holding%20a%20transparent%20umbrella%2C%20deep%20crimson%20and%20amber%20neon%20signs%20reflected%20in%20puddles%2C%20moody%20urban%20noir%20atmosphere%2C%20tasteful%20fully%20clothed%20cinematic%20still%2C%20subtle%20film%20grain%2C%20wide%20composition&width=1600&height=900&seq=hero-seoul-3am-02&orientation=landscape',
    kicker: '人气上升 · 韩国',
    headline: '首尔凌晨三点',
    caption: '便利店、末班车与一通迟到的电话，城市在深夜才肯说真话。',
  },
  {
    id: 'hero-3',
    movieId: 'glass-greenhouse',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20interior%20of%20a%20glass%20greenhouse%20at%20dusk%20with%20a%20woman%20in%20a%20dark%20green%20dress%20standing%20among%20tall%20plants%2C%20condensation%20on%20the%20glass%2C%20deep%20wine%20red%20and%20olive%20toned%20light%2C%20mysterious%20tasteful%20fully%20clothed%20editorial%20film%20photography%2C%20soft%20grain%2C%20wide%20atmospheric%20composition&width=1600&height=900&seq=hero-glass-greenhouse-03&orientation=landscape',
    kicker: '编辑推荐 · 悬疑',
    headline: '玻璃温室',
    caption: '她把每一株植物都剪成相同的形状，除了那一盆。',
  },
  {
    id: 'hero-4',
    movieId: 'han-river-summer',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20golden%20hour%20scene%20by%20the%20Han%20River%20with%20a%20woman%20in%20a%20flowing%20cream%20summer%20dress%20sitting%20on%20a%20riverside%20bench%2C%20warm%20sunset%20backlight%2C%20soft%20haze%2C%20tasteful%20fully%20clothed%20film%20photography%2C%20delicate%2035mm%20grain%2C%20wide%20peaceful%20composition&width=1600&height=900&seq=hero-han-river-04&orientation=landscape',
    kicker: '口碑佳作 · 恋爱',
    headline: '汉江之夏',
    caption: '夏天结束前，她想把没说完的话都写进一封信里。',
  },
  {
    id: 'hero-5',
    movieId: 'showa-afterglow',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20vintage%20scene%20inside%20a%20wooden%20Japanese%20inn%20corridor%20at%20evening%2C%20a%20woman%20in%20a%20traditional%20kimono%20seen%20from%20behind%20holding%20a%20paper%20lantern%2C%20warm%20amber%20light%2C%20dust%20particles%2C%20nostalgic%20Showa%20era%20atmosphere%2C%20tasteful%20fully%20clothed%20film%20still%2C%20soft%20grain&width=1600&height=900&seq=hero-showa-afterglow-05&orientation=landscape',
    kicker: '经典重制 · 时代剧',
    headline: '昭和残照',
    caption: '旧旅馆的最后一晚，灯还亮着，人已经不在了。',
  },
];

export const movies: Movie[] = [
  {
    id: 'rain-night-confession',
    title: '雨夜的告白',
    originalTitle: '雨の夜の告白',
    englishTitle: 'Confession on a Rainy Night',
    region: '日本',
    year: 2024,
    duration: 118,
    rating: 9.1,
    views: 1284000,
    likes: 42800,
    director: '高濑和也',
    cast: ['水岛凛', '黑川悠真', '野中千夏'],
    category: 'drama',
    tags: ['高分推荐', '唯美摄影', '雨夜', '情感细腻'],
    summary: '一场通宵不停的雨，让两个原本不会再见面的人困在同一间旧公寓里，把三年前没说完的话重新说了一遍。',
    synopsis: [
      '东京入梅后的第三周， freelance 摄影师佐仓在返回公寓的路上遇到了被雨困住的旧同学。两人上一次见面是在三年前的毕业典礼，那之后所有联系都停在了一句没有回复的消息上。',
      '整部影片几乎全部发生在同一间三十平米的旧公寓里，导演用窗外雨声的强弱变化来标记时间的流动。从最初的客套，到翻出旧照片，再到凌晨时那场迟到的告白，情绪的推进被拆成了非常多细小的动作：递毛巾、换热水、关掉又打开的灯。',
      '影片最后没有给出明确的结果，雨停了，两个人在楼下分开，各自走向不同的方向。镜头停在积水里倒映的路灯上，很久没有切开。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20movie%20poster%20portrait%20of%20a%20young%20woman%20in%20a%20cream%20trench%20coat%20standing%20on%20a%20rainy%20Tokyo%20street%20at%20night%2C%20soft%20backlight%2C%20deep%20red%20neon%20reflections%2C%20dark%20moody%20background%2C%20tasteful%20fully%20clothed%20editorial%20film%20photography%2C%2035mm%20grain%2C%20high%20detail&width=600&height=900&seq=mv-rain-night-11&orientation=portrait',
  },
  {
    id: 'seoul-3am',
    title: '首尔凌晨三点',
    originalTitle: '서울 새벽 세시',
    englishTitle: 'Seoul at 3AM',
    region: '韩国',
    year: 2025,
    duration: 124,
    rating: 8.9,
    views: 1562000,
    likes: 51200,
    director: '尹智浩',
    cast: ['韩瑞妍', '朴道贤', '李恩率'],
    category: 'urban',
    tags: ['城市夜景', '人气演员', '长镜头'],
    summary: '凌晨三点的首尔，一个便利店店员和一个刚下夜班的电台编导，用六个夜晚交换了彼此最不愿提起的事。',
    synopsis: [
      '影片以六个夜晚为章节，每一段都从凌晨三点开始。便利店成为整部作品唯一的固定场景，货架上的商品随季节更替，人物的处境却没有明显好转。',
      '导演大量使用固定长镜头，让演员在狭小的收银台前完成整段对话。城市的噪音从自动门开合的缝隙里漏进来，成为对话的节奏。',
      '第六个夜晚，电台编导带着一段从未播出的录音来到店里。录音里是她自己三年前的声音，那时候她还在讲别人的故事。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20black%20trench%20coat%20standing%20inside%20a%20bright%20convenience%20store%20at%20night%2C%20city%20lights%20through%20the%20window%2C%20cool%20neon%20accents%20with%20warm%20red%20highlights%2C%20moody%20tasteful%20fully%20clothed%20film%20photography%2C%20fine%20grain&width=600&height=900&seq=mv-seoul-3am-12&orientation=portrait',
  },
  {
    id: 'second-letter',
    title: '她的第二封信',
    originalTitle: '彼女の二通目の手紙',
    englishTitle: 'Her Second Letter',
    region: '日本',
    year: 2023,
    duration: 106,
    rating: 8.6,
    views: 892000,
    likes: 31600,
    director: '白石真澄',
    cast: ['森川葵', '长谷川奏', '大庭栞'],
    category: 'romance',
    tags: ['情感细腻', '治愈系', '女性视角'],
    summary: '整理旧宅时发现的一叠未寄出的信，把两个早已走散的人重新拉回同一段夏天。',
    synopsis: [
      '女主角在整理祖母留下的旧宅时，从衣柜顶层找到一叠写好却没有寄出的信。收信人的名字她从未听过，地址是海边一个已经废弃的车站。',
      '影片用信件的顺序作为叙事线索，每读一封就回到一个具体的夏天。热浪、风扇、蝉声和半融化的冰棍，构成了整部作品的记忆质感。',
      '最后一封信没有日期，只有一句「如果你先看到这封信，就当我从来没有写过」。她决定把信寄出去，地址写的是自己的旧居。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20pale%20linen%20dress%20holding%20an%20old%20stack%20of%20letters%20in%20a%20sunlit%20traditional%20Japanese%20room%2C%20dust%20in%20warm%20afternoon%20light%2C%20soft%20cream%20and%20amber%20tones%2C%20tasteful%20fully%20clothed%20editorial%20photography%2C%20delicate%20grain&width=600&height=900&seq=mv-second-letter-13&orientation=portrait',
  },
  {
    id: 'white-dove-red-street',
    title: '霓虹街的白鸽',
    originalTitle: '赤い街の白い鳩',
    englishTitle: 'White Dove on Neon Street',
    region: '日本',
    year: 2022,
    duration: 132,
    rating: 8.8,
    views: 1042000,
    likes: 38400,
    director: '河合宗介',
    cast: ['桐生遥', '椎名亮', '宫下玲'],
    category: 'drama',
    tags: ['高分推荐', '经典重制', '城市夜景'],
    summary: '在新宿一处即将拆迁的老楼里，三个互不相识的人因为一只受伤的白鸽短暂地住在了一起。',
    synopsis: [
      '老楼只剩下三户人家，拆迁通知已经贴了半年。某个台风夜，一只翅膀受伤的白鸽落进楼道，三个原本从不打招呼的住户第一次坐在了同一张桌子前。',
      '影片没有明确的主线，而是用三个人的日常片段拼出一个即将消失的街区。洗衣店的灯、楼下的自动贩卖机、夜里偶尔开过的改装车，都被拍得像证物一样认真。',
      '拆迁当天，白鸽的伤已经好了。三个人站在空荡的楼道里，谁也没有先开口说再见。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20white%20shirt%20standing%20in%20a%20narrow%20old%20Tokyo%20alley%20at%20dusk%2C%20red%20lanterns%20and%20neon%20signs%20glowing%20softly%2C%20melancholic%20warm%20red%20palette%2C%20tasteful%20fully%20clothed%20film%20photography%2C%20fine%20grain%2C%20high%20detail&width=600&height=900&seq=mv-white-dove-14&orientation=portrait',
  },
  {
    id: 'han-river-summer',
    title: '汉江之夏',
    originalTitle: '한강의 여름',
    englishTitle: 'Summer by the Han',
    region: '韩国',
    year: 2024,
    duration: 112,
    rating: 8.7,
    views: 1186000,
    likes: 40100,
    director: '徐敏京',
    cast: ['车秀雅', '金泰午', '文智媛'],
    category: 'romance',
    tags: ['唯美摄影', '治愈系', '情感细腻'],
    summary: '在汉江边经营自行车租赁的女孩，遇到了每年夏天都来还同一辆车的人。',
    synopsis: [
      '她守着一间快要倒闭的自行车租赁店，夏天的生意只够付水电。第七年的夏天，一个男人来还一辆七年前租走的车，车锁早就锈死了。',
      '影片几乎全部在户外拍摄，从傍晚的江风到夜里的帐篷灯，光线被记录得非常细。两个人沿着江边骑车、吃泡面、看别人放风筝，故事就这样一点点往前挪。',
      '夏天结束的时候，男人没有再来。她在那辆旧车的篮子里发现了一封信，还有一张七年前的租车单据。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20flowing%20cream%20summer%20dress%20standing%20beside%20a%20bicycle%20by%20a%20river%20at%20sunset%2C%20warm%20golden%20haze%2C%20soft%20bokeh%20city%20skyline%20in%20the%20distance%2C%20tasteful%20fully%20clothed%20film%20photography%2C%20gentle%20grain&width=600&height=900&seq=mv-han-river-15&orientation=portrait',
  },
  {
    id: 'glass-greenhouse',
    title: '玻璃温室',
    originalTitle: 'ガラスの温室',
    englishTitle: 'The Glass Greenhouse',
    region: '日本',
    year: 2025,
    duration: 121,
    rating: 9.3,
    views: 1678000,
    likes: 58600,
    director: '绪方冬马',
    cast: ['雨宫纱良', '南云慧', '小坂真琴'],
    category: 'suspense',
    tags: ['高分推荐', '反转结局', '密闭空间', '唯美摄影'],
    summary: '一座不对外的私人温室，一名只负责修剪植物的园艺师，和一份写着她自己名字的死亡证明。',
    synopsis: [
      '园艺师受雇照料一座位于山中的私人温室，雇主从不露面，所有指令都通过便签传达。温室里的每一株植物都被修剪成完全相同的形状，只有角落里的一盆从不允许触碰。',
      '第二幕开始，她在温室的地下室发现了一份旧档案。里面有一张死亡证明，姓名栏写的是她自己，日期是五年前。',
      '影片在最后二十分钟完成了两次反转，真正的答案藏在前半段反复出现的浇水时间里。结尾那一盆植物被移到了温室中央，镜头缓缓推近，画面切成全白。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20dark%20green%20dress%20standing%20inside%20a%20misty%20glass%20greenhouse%20surrounded%20by%20tall%20plants%2C%20moody%20low%20light%2C%20deep%20wine%20and%20olive%20tones%2C%20tasteful%20fully%20clothed%20editorial%20film%20photography%2C%20atmospheric%20grain&width=600&height=900&seq=mv-glass-greenhouse-16&orientation=portrait',
  },
  {
    id: 'last-subway',
    title: '末班地铁',
    originalTitle: '막차 지하철',
    englishTitle: 'The Last Subway',
    region: '韩国',
    year: 2023,
    duration: 98,
    rating: 8.4,
    views: 764000,
    likes: 27300,
    director: '郑夏恩',
    cast: ['闵书妍', '姜宇镇', '吴世琳'],
    category: 'urban',
    tags: ['城市夜景', '双线叙事', '长镜头'],
    summary: '一列已经停运的末班车里，两个乘客发现自己上的不是今晚的那一班。',
    synopsis: [
      '深夜两点，站台广播已经结束，两个人却同时上了同一节车厢。车门关上之后，窗外的站名开始重复出现。',
      '影片用两条时间线交替推进：一条在车厢里，一条在两人各自的白天。观众要到最后才会明白，重复的站名其实是两个人共同经历过的地点。',
      '列车最终停在一个没有名字的站台。车门打开，外面是白天。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20an%20oversized%20grey%20coat%20standing%20alone%20in%20an%20empty%20subway%20car%20at%20night%2C%20fluorescent%20light%2C%20dark%20window%20with%20tunnel%20lights%2C%20moody%20urban%20noir%2C%20tasteful%20fully%20clothed%20film%20photography%2C%20subtle%20grain&width=600&height=900&seq=mv-last-subway-17&orientation=portrait',
  },
  {
    id: 'showa-afterglow',
    title: '昭和残照',
    originalTitle: '昭和残照',
    englishTitle: 'Showa Afterglow',
    region: '日本',
    year: 2021,
    duration: 138,
    rating: 8.5,
    views: 623000,
    likes: 22800,
    director: '井上千秋',
    cast: ['藤木沙耶', '大仓祐一', '杉浦奈绪'],
    category: 'period',
    tags: ['复古胶片', '经典重制', '女性视角'],
    summary: '昭和末年，一间山中旅馆的最后一季。掌柜的女儿一边等一封不会来的信，一边把每个房间都擦得发亮。',
    synopsis: [
      '旅馆要关了，铁路改线之后不会再有客人上山。母亲病倒之后，所有事情都落在女儿一个人身上。她仍然每天铺床、烧水、擦走廊的木地板。',
      '影片用四个季节划分段落，每个季节都有一位不同的旅人短暂停留。他们带来的消息拼出了这个小镇即将消失的全貌。',
      '冬天，最后一盏纸灯笼被取下来。她关上门，没有回头。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20traditional%20kimono%20holding%20a%20paper%20lantern%20in%20a%20wooden%20Japanese%20inn%20corridor%20at%20night%2C%20warm%20amber%20light%2C%20nostalgic%20Showa%20era%20atmosphere%2C%20tasteful%20fully%20clothed%20film%20photography%2C%20soft%20grain&width=600&height=900&seq=mv-showa-afterglow-18&orientation=portrait',
  },
  {
    id: 'trainee-night',
    title: '练习生之夜',
    originalTitle: '연습생의 밤',
    englishTitle: 'Night of the Trainee',
    region: '韩国',
    year: 2024,
    duration: 104,
    rating: 8.2,
    views: 986000,
    likes: 34500,
    director: '卢基范',
    cast: ['申夏律', '白恩菲', '崔道允'],
    category: 'rookie',
    tags: ['人气演员', '城市夜景', '治愈系'],
    summary: '出道前的最后一夜，三个练习生决定把没跳完的那支舞跳完。',
    synopsis: [
      '公司通知明天解散团队的时候，已经是晚上十一点。三个人留在练习室，谁也没有先走。',
      '影片没有戏剧性的冲突，大部分时间都是反复的练习、喝水、躺在地板上聊天。镜子里映出的灯管坏了一根，一直没人修。',
      '天亮之前，她们把出道曲从头跳了一遍。摄像机在角落里一直开着。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20young%20woman%20in%20casual%20streetwear%20standing%20in%20a%20dim%20dance%20studio%20with%20mirrors%2C%20single%20warm%20lamp%2C%20soft%20red%20light%20spill%2C%20tasteful%20fully%20clothed%20editorial%20film%20photography%2C%20subtle%20grain&width=600&height=900&seq=mv-trainee-night-19&orientation=portrait',
  },
  {
    id: 'snow-country-inn',
    title: '雪国旅馆',
    originalTitle: '雪国の宿',
    englishTitle: 'Inn of the Snow Country',
    region: '日本',
    year: 2022,
    duration: 127,
    rating: 8.9,
    views: 1105000,
    likes: 39700,
    director: '佐伯直',
    cast: ['津岛绘里', '伊东慧', '望月千寻'],
    category: 'drama',
    tags: ['唯美摄影', '高分推荐', '情感细腻'],
    summary: '大雪封山的第七天，旅馆里只剩下老板娘和一位没能离开的客人。',
    synopsis: [
      '雪从周一一直下到周日，进山的路全部封闭。旅馆的客人陆续离开，最后只剩一位常年独居的作家。',
      '七天的相处被拍成了一部很安静的影片。他们一起扫雪、腌菜、修屋顶，谈话的内容从天气慢慢变成了各自的人生。',
      '雪停的那天早上，作家把写好的书稿留给老板娘，一个人走下山。她把书稿放进壁橱最深处，照常开始准备晚饭。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20thick%20cream%20knit%20sweater%20standing%20at%20the%20entrance%20of%20an%20old%20Japanese%20inn%20in%20heavy%20snow%2C%20warm%20interior%20light%20behind%20her%2C%20soft%20blue%20grey%20winter%20evening%2C%20tasteful%20fully%20clothed%20film%20photography&width=600&height=900&seq=mv-snow-inn-20&orientation=portrait',
  },
  {
    id: 'afternoon-orchard',
    title: '午后果园',
    originalTitle: '오후의 과수원',
    englishTitle: 'Afternoon Orchard',
    region: '韩国',
    year: 2025,
    duration: 109,
    rating: 8.3,
    views: 542000,
    likes: 19800,
    director: '赵恩率',
    cast: ['李多恩', '韩志勋', '金娜英'],
    category: 'romance',
    tags: ['治愈系', '唯美摄影', '女性视角'],
    summary: '为了处理外婆留下的果园，她请了一个月的假，也遇到了每年都来帮忙摘果子的那个人。',
    synopsis: [
      '果园已经荒了两年，苹果树还在结果，只是没人摘。她原本打算卖掉土地，然后再也不回来。',
      '一个每年都来帮忙的邻居教她剪枝、套袋、分级。一个月的假期被拉得很长，长到她开始记得每一棵树的位置。',
      '最后那批苹果卖出去的时候，她留了一筐，放在外婆常坐的藤椅旁边。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20straw%20hat%20and%20pale%20shirt%20standing%20in%20a%20sunlit%20apple%20orchard%2C%20warm%20golden%20afternoon%20light%20through%20leaves%2C%20soft%20green%20and%20cream%20palette%2C%20tasteful%20fully%20clothed%20editorial%20photography%2C%20gentle%20grain&width=600&height=900&seq=mv-afternoon-orchard-21&orientation=portrait',
  },
  {
    id: 'seventh-tenant',
    title: '第七位租客',
    originalTitle: '일곱 번째 세입자',
    englishTitle: 'The Seventh Tenant',
    region: '韩国',
    year: 2021,
    duration: 115,
    rating: 8.6,
    views: 738000,
    likes: 26400,
    director: '权泰成',
    cast: ['河敏书', '徐允在', '朴世罗'],
    category: 'suspense',
    tags: ['反转结局', '密闭空间', '高分推荐'],
    summary: '一栋只有六个房间的老公寓，房东却坚持说这里住过七个人。',
    synopsis: [
      '新租客搬进来的第一天，就在信箱里发现了一封写给「七号房」的信。房东说这栋楼只有六个房间，从来没有七号。',
      '影片的叙事重心不在灵异，而在人与人之间被刻意隐瞒的部分。每个租客都记得不一样版本的往事，唯一一致的是那个总是关着的储物间。',
      '结尾储物间的门被打开，里面只有一面镜子和一把椅子。镜头切到镜子里的房间，门牌上是数字七。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20neutral%20beige%20coat%20standing%20in%20a%20dim%20old%20apartment%20stairwell%20holding%20a%20single%20envelope%2C%20single%20ceiling%20lamp%2C%20muted%20red%20brown%20tones%2C%20mysterious%20tasteful%20fully%20clothed%20film%20photography&width=600&height=900&seq=mv-seventh-tenant-22&orientation=portrait',
  },
  {
    id: 'kyoto-umbrella',
    title: '京都雨伞',
    originalTitle: '京都の傘',
    englishTitle: 'Umbrella in Kyoto',
    region: '日本',
    year: 2024,
    duration: 130,
    rating: 8.8,
    views: 826000,
    likes: 30100,
    director: '西园寺保',
    cast: ['绫濑千鹤', '三宅悠', '高木沙罗'],
    category: 'period',
    tags: ['复古胶片', '唯美摄影', '情感细腻'],
    summary: '一间只修伞不卖伞的老店，和一位每年梅雨季都来取同一把伞的客人。',
    synopsis: [
      '伞店开在京都一条几乎没有游客的小巷里，第三代店主只会修伞。梅雨季刚开始，一位客人拿着一把断了骨的旧伞来修，说这把伞已经在这里修过很多次。',
      '影片以梅雨季为时间轴，从入梅到出梅，一共四十天。伞店的账本被拍得极细，每一笔都用毛笔记录。',
      '出梅那天，伞修好了，客人却没有来。店主把伞挂回原位，账本上多写了一行字。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20muted%20green%20kimono%20holding%20a%20traditional%20paper%20umbrella%20on%20a%20wet%20stone%20alley%20in%20Kyoto%20during%20rainy%20season%2C%20warm%20lantern%20glow%2C%20soft%20mist%2C%20tasteful%20fully%20clothed%20film%20photography%2C%20fine%20grain&width=600&height=900&seq=mv-kyoto-umbrella-23&orientation=portrait',
  },
  {
    id: 'neon-and-sea',
    title: '霓虹与海',
    originalTitle: '네온과 바다',
    englishTitle: 'Neon and the Sea',
    region: '韩国',
    year: 2023,
    duration: 119,
    rating: 8.5,
    views: 907000,
    likes: 31200,
    director: '洪瑞镇',
    cast: ['尹彩英', '南宫民', '表智慧'],
    category: 'urban',
    tags: ['城市夜景', '长镜头', '人气演员'],
    summary: '在港口城市做夜间代驾的女孩，把每一位客人的目的地都记在笔记本上。',
    synopsis: [
      '她做代驾已经四年，笔记本上记满了陌生的地址。有些地址重复出现，有些只出现一次，她都记了下来。',
      '影片跟着她的夜间路线展开，从港口一直开到城市另一头的高架。乘客上下车，故事片段被留在后座。',
      '最后一单的地址是她自己的旧居。她没有下车，只是把车停在楼下，等到天亮。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20black%20leather%20jacket%20leaning%20against%20a%20car%20at%20night%20in%20a%20port%20city%2C%20neon%20signs%20and%20harbor%20lights%20reflected%20on%20wet%20asphalt%2C%20deep%20crimson%20accents%2C%20tasteful%20fully%20clothed%20editorial%20film%20photography&width=600&height=900&seq=mv-neon-sea-24&orientation=portrait',
  },
  {
    id: 'person-in-mirror',
    title: '镜中之人',
    originalTitle: '鏡の中のひと',
    englishTitle: 'The Person in the Mirror',
    region: '日本',
    year: 2025,
    duration: 113,
    rating: 9.0,
    views: 1321000,
    likes: 44900,
    director: '长野由纪',
    cast: ['相乐美月', '片冈奏多', '小野寺凛'],
    category: 'suspense',
    tags: ['反转结局', '双线叙事', '高分推荐'],
    summary: '一位舞台剧女演员在排练新戏时发现，剧本里写的正是她还没有经历过的生活。',
    synopsis: [
      '新戏的剧本寄到家里时没有署名。女主角越读越觉得不对，剧本里描述的公寓、邻居、甚至一句口头禅，都与她的日常完全一致。',
      '排练开始之后，剧本里写的事情开始一件件发生。她想退出，却发现合同上的签名是自己从未写过的字迹。',
      '首演当晚，舞台上的镜子里站着一个和她一模一样的人。灯光暗下，观众席响起掌声。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20dark%20burgundy%20stage%20dress%20standing%20in%20front%20of%20a%20large%20theatre%20mirror%20with%20warm%20spotlight%2C%20deep%20shadows%2C%20mysterious%20theatrical%20atmosphere%2C%20tasteful%20fully%20clothed%20film%20photography%2C%20of%20soft%20grain&width=600&height=900&seq=mv-person-mirror-25&orientation=portrait',
  },
  {
    id: 'spring-theatre',
    title: '春日剧场',
    originalTitle: '봄날의 극장',
    englishTitle: 'Spring Theatre',
    region: '韩国',
    year: 2022,
    duration: 122,
    rating: 8.4,
    views: 615000,
    likes: 21300,
    director: '裴允书',
    cast: ['金多贤', '柳在民', '赵雅凛'],
    category: 'drama',
    tags: ['情感细腻', '治愈系', '女性视角'],
    summary: '一间即将停业的老剧场，放映员决定在最后一个月把库存的胶片全部放完。',
    synopsis: [
      '剧场只有两个放映厅，胶片库存超过三百卷。停业通知下来之后，放映员决定每天放两部，直到放完为止。',
      '来看的人越来越少，最后几场只有一位常客。两个人隔着放映室的小窗聊天，聊的都是与电影无关的事。',
      '最后一天放的是没有片名的旧胶片，画面里是这间剧场刚开业时的样子。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20soft%20beige%20cardigan%20standing%20in%20the%20projection%20booth%20of%20an%20old%20cinema%2C%20warm%20projector%20light%20beam%20and%20dust%2C%20red%20velvet%20seats%20below%2C%20tasteful%20fully%20clothed%20film%20photography&width=600&height=900&seq=mv-spring-theatre-26&orientation=portrait',
  },
  {
    id: 'seaside-record',
    title: '海边旧唱片',
    originalTitle: '海辺の古いレコード',
    englishTitle: 'Old Record by the Sea',
    region: '日本',
    year: 2023,
    duration: 107,
    rating: 8.6,
    views: 702000,
    likes: 24800,
    director: '小柳启',
    cast: ['夏川澪', '阿部亮平', '远藤沙英'],
    category: 'romance',
    tags: ['复古胶片', '治愈系', '唯美摄影'],
    summary: '海边一间二手唱片店里，一张没有封套的黑胶让两个陌生人聊了整整一个下午。',
    synopsis: [
      '唱片店开在海堤下面，涨潮的时候门口会被水淹一半。店主是个不太说话的年轻人，店里最贵的一张唱片从来不卖。',
      '一个下雨的下午，一位客人拿起那张唱片，说出了它的来历。那是她父亲年轻时录的，只有一张。',
      '雨停之后，店主把唱片放进唱机。两个人坐在门口，听完了一整面。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20woman%20in%20a%20light%20blue%20shirt%20holding%20a%20vinyl%20record%20inside%20a%20small%20seaside%20record%20store%2C%20warm%20afternoon%20light%20through%20the%20window%2C%20soft%20cream%20and%20muted%20red%20tones%2C%20tasteful%20fully%20clothed%20film%20photography&width=600&height=900&seq=mv-seaside-record-27&orientation=portrait',
  },
  {
    id: 'midnight-radio',
    title: '午夜电台',
    originalTitle: '자정의 라디오',
    englishTitle: 'Midnight Radio',
    region: '韩国',
    year: 2024,
    duration: 101,
    rating: 8.1,
    views: 478000,
    likes: 17600,
    director: '申河镇',
    cast: ['白智媛', '具承民', '卢秀彬'],
    category: 'rookie',
    tags: ['人气演员', '城市夜景', '治愈系'],
    summary: '一个只有深夜时段的新人 DJ，和一位每晚都打进来却从不说话的听众。',
    synopsis: [
      '电台的深夜档收听率一直垫底，节目组已经准备撤掉。新人 DJ 每天照常读听众来信，念完就放歌。',
      '连续三周，同一个号码每晚都会打进来，接通之后没有任何声音。她开始为这个沉默的来电留一段固定的时间。',
      '节目被保留了下来。最后一期，电话没有响，她在节目里读了一封写给陌生听众的信。',
    ],
    cover:
      'https://readdy.ai/api/search-image?query=cinematic%20vertical%20poster%20of%20a%20young%20woman%20wearing%20headphones%20in%20a%20dim%20radio%20studio%20at%20night%2C%20warm%20desk%20lamp%20and%20glowing%20mixer%20lights%2C%20deep%20red%20and%20amber%20accents%2C%20tasteful%20fully%20clothed%20editorial%20film%20photography&width=600&height=900&seq=mv-midnight-radio-28&orientation=portrait',
  },
];

export const movieStats = {
  total: 18,
  regions: 2,
  categories: 6,
  updatedAt: '2026-10-18',
};